import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { isExternalLayout, registerExternalLayout, unregisterExternalLayout } from './ExternalLayoutLoader';
import { ExternalLayoutProvider, useExternalSkinProps } from './ExternalLayoutWrapper';
import { searchSkins } from '../services/skinApi';
import { getSkinDataById } from '../utils/registrySkinsCache';

// 레이아웃 지연 로딩 구현
const KorEnvironmentLayout = React.lazy(() => import('./kor-environment/src/Layout'));
const FloneLayout = React.lazy(() => import('./flone/layouts/LayoutOne')); // 경로 수정
const FinappLayout = React.lazy(() => import('./finapp/layouts/LayoutMain')); // Finapp 레이아웃 추가
const GpcLayout = React.lazy(() => import('./gpc/layouts/LayoutMain')); // Gpc 레이아웃 추가
const MidasLayout = React.lazy(() => import('./midas/layouts/LayoutMain')); // Midas 레이아웃 추가
const BfLayout = React.lazy(() => import('./bf/LayoutMain')); // BF 레이아웃 추가

// 🚀 React 컴포넌트 지연 로딩 구현 (finapp용)
const FinappComponent = React.lazy(() => import('../components/finapp')); // finapp React 컴포넌트
const GpcComponent = React.lazy(() => import('../components/gpc')); // gpc React 컴포넌트
const MidasComponent = React.lazy(() => import('../components/midas')); // midas React 컴포넌트

// 로딩 중 표시할 컴포넌트
const LoadingLayout = ({ children }) => {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column'
    }}>
      <div style={{ marginBottom: '20px' }}>레이아웃 로딩 중...</div>
      {children}
    </div>
  );
};

// 레지스트리 스킨을 외부 스킨으로 변환하여 로드
const RegistrySkinRenderer = ({ skinData, ...props }) => {
  const [externalLayoutId, setExternalLayoutId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const registerAsTempExternalSkin = async () => {
      try {


        const cssUrl = skinData.latestVersion?.cssFileUrl;
        const jsUrl = skinData.latestVersion?.jsFileUrl;
        

        if (!jsUrl) {
          console.error('❌ JS 파일 URL이 없습니다!');
          console.error('❌ 스킨 데이터 상세:', {
            skinId: skinData.id,
            skinName: skinData.name,
            hasPermission: skinData.hasPermission,
            latestVersion: skinData.latestVersion,
            allVersions: skinData.versions
          });
          
          // 대체 URL 경로 시도
          let fallbackJsUrl = null;
          
          // 1. 다른 버전에서 jsFileUrl 찾기
          if (skinData.versions && skinData.versions.length > 0) {
            for (const version of skinData.versions) {
              if (version.jsFileUrl) {
                fallbackJsUrl = version.jsFileUrl;
                break;
              }
            }
          }
          
          // 2. 패턴 기반 URL 생성 시도
          if (!fallbackJsUrl && skinData.id) {
            const possibleUrls = [
              `https://withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skins/${skinData.id}/latest/index.js`,
              `https://withcookie-bucket.s3.ap-northeast-2.amazonaws.com/skin/${skinData.id}/latest/index.js`,
              `https://cdn.withcookie-refrigerator.com/skins/${skinData.id}/latest/index.js`,
              `https://cdn.withcookie-refrigerator.com/skin/${skinData.id}/latest/index.js`
            ];
            
            // 첫 번째 가능한 URL 사용 (실제로는 HEAD 요청으로 확인해야 하지만 일단 시도)
            fallbackJsUrl = possibleUrls[0];
          }
          
          if (fallbackJsUrl) {
            // fallbackJsUrl을 jsUrl로 사용
            skinData.latestVersion = {
              ...skinData.latestVersion,
              jsFileUrl: fallbackJsUrl
            };
          } else {
            // 모든 대체 방법이 실패한 경우
            throw new Error(`JS 파일 URL을 찾을 수 없습니다. 스킨 ID: ${skinData.id}, 이름: ${skinData.name}. 스킨이 올바르게 업로드되었는지 확인해주세요.`);
          }
        }

        // 스킨 ID 기반 전역 변수명 규칙 적용
        const primaryGlobalName = `ExternalSkin${skinData.id}`;

        // 임시 외부 레이아웃 ID 생성
        const tempLayoutId = `temp_registry_${skinData.id}`;
        
        // 외부 스킨 설정 생성
        const externalSkinConfig = {
          id: tempLayoutId,
          name: `${skinData.name} (레지스트리)`,
          description: skinData.description,
          version: skinData.latestVersion?.version || '1.0.0',
          author: 'Registry',
          umdUrl: jsUrl,
          globalName: primaryGlobalName, // 스킨 ID 기반 규칙 사용
          cssUrls: cssUrl ? [cssUrl] : [],
          jsUrls: [],
          isRegistrySkin: true, // 레지스트리 스킨 마커 추가
          skinId: skinData.id // 스킨 ID 추가 (fallback용)
        };


        // 외부 스킨으로 등록
        registerExternalLayout(externalSkinConfig);
        
        setExternalLayoutId(tempLayoutId);
        
      } catch (err) {
        console.error('레지스트리 스킨을 외부 스킨으로 등록 실패:', err);
        setError(err.message);
      }
    };

    registerAsTempExternalSkin();

    // 클린업 시 임시 외부 스킨 제거
    return () => {
      if (externalLayoutId) {
        try {
          unregisterExternalLayout(externalLayoutId);
        } catch (err) {
          console.warn('임시 외부 스킨 제거 실패:', err);
        }
      }
    };
  }, [skinData]);

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        color: '#dc3545',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
          레지스트리 스킨 로드 실패
        </div>
        <div style={{ fontSize: '16px', marginBottom: '10px', color: '#666' }}>
          스킨: {skinData.name} (ID: {skinData.id})
        </div>
        <div style={{ 
          fontSize: '14px', 
          marginBottom: '20px', 
          maxWidth: '600px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}>
          {error}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '20px' }}>
          가능한 원인:
          <ul style={{ textAlign: 'left', marginTop: '10px' }}>
            <li>스킨 파일이 올바르게 업로드되지 않았습니다</li>
            <li>스킨 파일에 대한 접근 권한이 없습니다</li>
            <li>스킨 서버에 일시적인 문제가 발생했습니다</li>
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
          <button 
            onClick={() => window.history.back()} 
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            이전으로
          </button>
        </div>
      </div>
    );
  }

  if (!externalLayoutId) {
    return (
      <LoadingLayout>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', marginBottom: '10px' }}>
            레지스트리 스킨 로딩 중...
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            스킨: {skinData.name}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
            스킨 ID: {skinData.id}
          </div>
        </div>
      </LoadingLayout>
    );
  }

  // 외부 스킨 프로바이더 사용
  
  return (
    <ExternalLayoutProvider 
      layoutId={externalLayoutId} 
      {...props}
    />
  );
};

// 레지스트리 스킨 래퍼 컴포넌트 (외부 스킨과 같은 방식으로 props 제공)
const RegistryLayoutWrapper = ({ layoutId, skinData: propSkinData, children, ...layoutProps }) => {
  // 외부 스킨과 동일한 props 구조 생성
  const skinProps = useExternalSkinProps({ ...layoutProps, children });
  
  // fallback용 상태 (항상 선언)
  const [skinData, setSkinData] = useState(null);
  const [loading, setLoading] = useState(false); // 캐싱 시스템으로 인해 기본적으로 false
  const [error, setError] = useState(null);
  
  // Promise이거나 불완전한 데이터인 경우 useEffect로 처리 (항상 호출되어야 함)
  useEffect(() => {
    let isMounted = true;

    const loadSkinData = async () => {
      try {
        let loadedSkinData = null;
        
        // 1. propSkinData가 Promise인 경우 먼저 resolve 시도
        if (propSkinData instanceof Promise) {
          try {
            loadedSkinData = await propSkinData;
          } catch (promiseError) {
            console.error('❌ propSkinData Promise resolve 실패:', promiseError);
            loadedSkinData = null;
          }
        }
        
        // 2. Promise resolve가 실패했거나 layoutId로 로드해야 하는 경우
        if (!loadedSkinData && layoutId) {
          // registry_ prefix 제거하여 실제 스킨 ID 추출
          const skinId = layoutId.replace('registry_', '');
          

          // 새로운 캐싱 시스템 사용 (즉시 응답, 로딩 상태 최소화)
          loadedSkinData = await getSkinDataById(skinId);
          
        }
        
        if (loadedSkinData) {

          if (isMounted) {
            setSkinData(loadedSkinData);
          }
          return;
        }
        
        throw new Error(`스킨 데이터를 찾을 수 없습니다. layoutId: ${layoutId}`);
        
      } catch (err) {
        console.error('💥 레지스트리 스킨 로드 실패:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(true); // 에러 시에만 로딩 상태 표시
        }
      }
    };

    loadSkinData();

    return () => {
      isMounted = false;
    };
  }, [layoutId, propSkinData]); // propSkinData도 dependency에 추가


  // Promise가 아니고 완전한 데이터인 경우 즉시 렌더링
  if (propSkinData && !(propSkinData instanceof Promise) && propSkinData.id) {

    return <RegistrySkinRenderer skinData={propSkinData} {...skinProps} />;
  }

  if (loading) {
    return (
      <LoadingLayout>
        <div>레지스트리 스킨 로딩 중...</div>
      </LoadingLayout>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        color: '#dc3545',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
          레지스트리 스킨 로드 실패
        </div>
        <div style={{ fontSize: '16px', marginBottom: '10px', color: '#666' }}>
          스킨 ID: {layoutId}
        </div>
        <div style={{ 
          fontSize: '14px', 
          marginBottom: '20px', 
          maxWidth: '600px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px'
        }}>
          {error}
        </div>
        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '20px' }}>
          가능한 원인:
          <ul style={{ textAlign: 'left', marginTop: '10px' }}>
            <li>스킨 권한이 없거나 만료되었습니다</li>
            <li>스킨 데이터가 불완전하거나 손상되었습니다</li>
            <li>네트워크 연결에 문제가 있습니다</li>
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
          <button 
            onClick={() => window.history.back()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            이전으로
          </button>
        </div>
      </div>
    );
  }

  if (!skinData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div>스킨 데이터를 찾을 수 없습니다.</div>
      </div>
    );
  }

  // 스킨 데이터가 있으면 레지스트리 스킨 렌더러 사용
  
  // 🔧 외부 스킨이 메뉴 데이터에 접근할 수 있도록 데이터 구조 평면화
  const flattenedProps = {
    ...skinProps,
    // 메뉴 데이터를 최상위 레벨로 이동
    previewData: skinProps.data?.previewData || (skinProps.data?.menus ? { menus: skinProps.data.menus } : undefined),
    editorData: skinProps.data?.editorData || (skinProps.data?.menus ? { menus: skinProps.data.menus } : undefined),
    globalMenus: skinProps.data?.globalMenus || [],
    mainMenus: skinProps.data?.mainMenus || [],
    withcookieData: skinProps.data?.withcookieData || null,
    // 기타 필요한 데이터들
    navigation: skinProps.data?.navigation || null,
    menus: skinProps.data?.menus || [],
    user: skinProps.data?.user || null,
    company: skinProps.data?.company || null
  };
  
  
  return <RegistrySkinRenderer skinData={skinData} {...flattenedProps} />;
};

// 레이아웃별 CSS/JS 파일 정의
const LAYOUT_ASSETS = {
  'kor-environment': {
    css: [
      '/assets_kor-environment/css/bootstrap.min.css',
      '/assets_kor-environment/css/fontawesome-all.min.css',
      '/assets_kor-environment/css/animate.min.css',
      '/assets_kor-environment/css/style.css',
      '/assets_kor-environment/css/responsive.css',
      '/assets_kor-environment/css/owl.carousel.min.css',
      '/assets_kor-environment/css/owl.theme.default.min.css',
      '/assets_kor-environment/css/magnific-popup.css',
      '/assets_kor-environment/css/jquery-ui.css',
      'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap'
    ],
    js: [
      '/assets_kor-environment/js/jquery.min.js',
      '/assets_kor-environment/js/bootstrap.bundle.min.js',
      '/assets_kor-environment/js/waypoints.min.js',
      '/assets_kor-environment/js/jquery.counterup.min.js',
      '/assets_kor-environment/js/owl.carousel.min.js',
      '/assets_kor-environment/js/jquery.magnific-popup.min.js',
      '/assets_kor-environment/js/wow.js',
      '/assets_kor-environment/js/appear.js',
      '/assets_kor-environment/js/theme.js'
    ]
  },
  'flone': {
    css: [
      // 'https://fonts.googleapis.com/css?family=Work+Sans:100,200,300,400,500,600,700,800,900',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css',
      '/assets_flone/css/bootstrap.min.css',
      '/assets_flone/css/icons.min.css',
      '/assets_flone/css/plugins.css',
      '/assets_flone/css/style.css',
    ],
    js: [
      '/assets_flone/js/vendor/modernizr-3.6.0.min.js',
      '/assets_flone/js/vendor/jquery-3.6.0.min.js',
      '/assets_flone/js/vendor/bootstrap.bundle.min.js',
      '/assets_flone/js/plugins.js',
      '/assets_flone/js/main.js'
    ]
  },
  'finapp': {
    css: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css',
      '/assets_finapp/css/style.css'
    ],
    js: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js',
      'https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js',
      '/assets_finapp/js/plugins/bootstrap.bundle.min.js',
      '/assets_finapp/js/base.js'
    ]
  },
  // 🚀 finapp React 컴포넌트용 에셋 설정
  'finapp-component': {
    css: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css'
    ],
    js: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js',
      'https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js'
    ]
  },
  // 🚀 gpc React 컴포넌트용 에셋 설정
  'gpc-component': {
    css: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css'
    ],
    js: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js',
      'https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js'
    ]
  },
  // 🚀 midas React 컴포넌트용 에셋 설정
  'midas-component': {
    css: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css'
    ],
    js: [
      'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js',
      'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js',
      'https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js'
    ]
  },
  // BF 레이아웃용 에셋 설정
  'bf': {
    css: [
      // BF 레이아웃은 자체 스타일을 사용
      // app.css는 컴포넌트에서 직접 import
    ],
    js: [
      // 필요한 JS 파일이 있다면 여기에 추가
    ]
  }
};

// 이미 로드된 애셋 추적
const loadedAssets = {
  css: new Set(),
  js: new Set()
};

// CSS 파일 동적 로드 함수 (스킨용 - 높은 우선순위)
const loadSkinCssFile = (url) => {
  // 이미 로드된 파일이면 스킵
  if (loadedAssets.css.has(url)) {
    return Promise.resolve(url);
  }
  
  return new Promise((resolve) => {
    try {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      
      // 스킨 CSS임을 표시하는 속성 추가
      link.setAttribute('data-skin-css', 'true');
      
      // 타임아웃 설정 (10초)
      const timeout = setTimeout(() => {
        console.warn(`스킨 CSS 로드 타임아웃: ${url}`);
        resolve(url); // 타임아웃 시에도 계속 진행
      }, 10000);
      
      link.onload = () => {
        clearTimeout(timeout);
        loadedAssets.css.add(url);
        resolve(url);
      };
      
      link.onerror = () => {
        clearTimeout(timeout);
        console.error(`스킨 CSS 로드 실패: ${url}`);
        resolve(url); // 에러 시에도 계속 진행
      };
      
      // 다른 CSS 파일들보다 뒤에 추가하여 우선순위 높이기
      document.head.appendChild(link);
    } catch (error) {
      console.error(`스킨 CSS 로드 예외 발생: ${url}`, error);
      resolve(url); // 예외 발생 시에도 계속 진행
    }
  });
};

// CSS 파일 동적 로드 함수
const loadCssFile = (url) => {
  // 이미 로드된 파일이면 스킵
  if (loadedAssets.css.has(url)) {
    return Promise.resolve(url);
  }
  
  return new Promise((resolve) => {
    try {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      
      // 타임아웃 설정 (10초)
      const timeout = setTimeout(() => {
        console.warn(`CSS 로드 타임아웃: ${url}`);
        resolve(url); // 타임아웃 시에도 계속 진행
      }, 10000);
      
      link.onload = () => {
        clearTimeout(timeout);
        loadedAssets.css.add(url);
        resolve(url);
      };
      
      link.onerror = () => {
        clearTimeout(timeout);
        console.error(`CSS 로드 실패: ${url}`);
        resolve(url); // 에러 시에도 계속 진행
      };
      
      document.head.appendChild(link);
    } catch (error) {
      console.error(`CSS 로드 예외 발생: ${url}`, error);
      resolve(url); // 예외 발생 시에도 계속 진행
    }
  });
};


// JS 파일 동적 로드 함수 (일반 스크립트용)
const loadJsFile = (url) => {
  // 이미 로드된 파일이면 스킵
  if (loadedAssets.js.has(url)) {
    return Promise.resolve(url);
  }
  
  return new Promise((resolve) => {
    try {
      const script = document.createElement('script');
      script.src = url;
      script.async = false;
      script.crossOrigin = 'anonymous';
      
      const timeout = setTimeout(() => {
        console.warn(`JS 로드 타임아웃: ${url}`);
        resolve(url);
      }, 15000);
      
      script.onload = () => {
        clearTimeout(timeout);
        loadedAssets.js.add(url);
        resolve(url);
      };
      
      script.onerror = (error) => {
        clearTimeout(timeout);
        console.error(`JS 로드 실패: ${url}`, error);
        resolve(url);
      };
      
      document.head.appendChild(script);
    } catch (error) {
      console.error(`JS 로드 예외 발생: ${url}`, error);
      resolve(url);
    }
  });
};

// 레이아웃 애셋 로드 함수
const loadLayoutAssets = async (layoutName) => {
  const assets = LAYOUT_ASSETS[layoutName];
  if (!assets) return;
  
  // CSS 파일 로드 (먼저 로드하고 기다림)
  if (assets.css && assets.css.length > 0) {
    // CSS 파일들을 동시에 로드 (병렬 로드)
    const cssPromises = assets.css.map(url => loadCssFile(url));
    await Promise.all(cssPromises);
  }
  
  // JS 파일 로드 (CSS 로드 후 순차적으로 로드)
  if (assets.js && assets.js.length > 0) {
    // JS 파일들을 순차적으로 로드 (순서 보장)
    for (const url of assets.js) {
      await loadJsFile(url);
    }
  }
  
  // 특정 레이아웃의 특별한 처리
  if (layoutName === 'kor-environment') {
    // 환경협회 레이아웃의 경우 theme.js가 제대로 실행되기 위한 추가 대기 시간
    await new Promise(resolve => setTimeout(resolve, 300));
  }
  
  // Finapp 레이아웃의 특별한 처리
  if (layoutName === 'finapp') {
    // Splide 초기화를 위한 코드
    if (window.Splide) {
      document.addEventListener('DOMContentLoaded', () => {
        // 페이지 로드 후 Splide 초기화 코드 실행
        const carouselSingle = document.querySelector('.carousel-single');
        if (carouselSingle) {
          new window.Splide('.carousel-single', {
            perPage: 1,
            rewind: true,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: true
          }).mount();
        }
        
        const carouselMultiple = document.querySelector('.carousel-multiple');
        if (carouselMultiple) {
          new window.Splide('.carousel-multiple', {
            perPage: 2,
            rewind: true,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: true,
            breakpoints: {
              768: {
                perPage: 2
              },
              500: {
                perPage: 1
              }
            }
          }).mount();
        }
        
        const carouselSmall = document.querySelector('.carousel-small');
        if (carouselSmall) {
          new window.Splide('.carousel-small', {
            perPage: 9,
            rewind: false,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: false,
            breakpoints: {
              768: {
                perPage: 5
              },
              500: {
                perPage: 4
              }
            }
          }).mount();
        }
      });
    }
  }
  
  // Finapp 레이아웃의 특별한 처리
  if (layoutName === 'gpc') {
    // Splide 초기화를 위한 코드
    if (window.Splide) {
      document.addEventListener('DOMContentLoaded', () => {
        // 페이지 로드 후 Splide 초기화 코드 실행
        const carouselSingle = document.querySelector('.carousel-single');
        if (carouselSingle) {
          new window.Splide('.carousel-single', {
            perPage: 1,
            rewind: true,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: true
          }).mount();
        }
        
        const carouselMultiple = document.querySelector('.carousel-multiple');
        if (carouselMultiple) {
          new window.Splide('.carousel-multiple', {
            perPage: 2,
            rewind: true,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: true,
            breakpoints: {
              768: {
                perPage: 2
              },
              500: {
                perPage: 1
              }
            }
          }).mount();
        }
        
        const carouselSmall = document.querySelector('.carousel-small');
        if (carouselSmall) {
          new window.Splide('.carousel-small', {
            perPage: 9,
            rewind: false,
            type: 'loop',
            gap: 16,
            padding: 16,
            arrows: false,
            pagination: false,
            breakpoints: {
              768: {
                perPage: 5
              },
              500: {
                perPage: 4
              }
            }
          }).mount();
        }
      });
    }
  }
};

// 등록된 모든 레이아웃 맵
const LAYOUTS = {
  'kor-environment': KorEnvironmentLayout,
  'flone': FloneLayout,
  'finapp': FinappLayout, // Finapp 레이아웃 추가
  'gpc': GpcLayout, // Gpc 레이아웃 추가
  'midas': MidasLayout, // Midas 레이아웃 추가
  'finapp-component': FinappComponent, // 🚀 Finapp React 컴포넌트 추가
  'gpc-component': GpcComponent, // 🚀 Gpc React 컴포넌트 추가
  'midas-component': MidasComponent, // 🚀 Midas React 컴포넌트 추가
  'bf': BfLayout, // BF 레이아웃 추가
  // 여기에 더 많은 레이아웃 추가 가능
};

// 레이아웃 이름으로 컴포넌트 가져오기
export const getLayout = (layoutName, skinData = null) => {
  // 레지스트리 스킨인지 확인
  if (layoutName && layoutName.startsWith('registry_')) {
    return (props) => {
      return React.createElement(RegistryLayoutWrapper, { 
        layoutId: layoutName,
        skinData: skinData,
        ...props 
      });
    };
  }
  
  // 외부 레이아웃인지 확인
  if (isExternalLayout(layoutName)) {
    return (props) => {
      return (
        <ExternalLayoutProvider 
          layoutId={layoutName} 
          {...props}
        />
      );
    };
  }
  
  // 내장 레이아웃 처리
  const Layout = LAYOUTS[layoutName] || LAYOUTS['flone']; // 기본값 설정
  
  return (props) => {
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    
    // 레이아웃 변경 시 필요한 CSS/JS 파일 로드
    useEffect(() => {
      if (!layoutName) return;
      
      let isMounted = true;
      
      const loadAssets = async () => {
        try {
          // 애셋 로드 시작
          await loadLayoutAssets(layoutName);
          
          // 컴포넌트가 여전히 마운트된 상태인지 확인
          if (isMounted) {
            setAssetsLoaded(true);
          }
        } catch (error) {
          console.error(`${layoutName} 레이아웃 애셋 로드 실패:`, error);
          // 에러가 발생해도 렌더링은 진행
          if (isMounted) {
            setAssetsLoaded(true);
          }
        }
      };
      
      loadAssets();
      
      // 클린업 함수
      return () => {
        isMounted = false;
      };
    }, [layoutName]);
    
    // 애셋이 로드될 때까지 대기
    if (!assetsLoaded) {
      return <LoadingLayout>{props.children}</LoadingLayout>;
    }
    
    return (
      <Suspense fallback={<LoadingLayout>{props.children}</LoadingLayout>}>
        <Layout {...props} />
      </Suspense>
    );
  };
};

// 사용 가능한 모든 레이아웃 이름 가져오기
export const getAllLayoutNames = () => {
  // 내장 레이아웃과 외부 레이아웃 모두 포함
  const internalLayouts = Object.keys(LAYOUTS);
  const externalLayouts = isExternalLayout ? [] : []; // ExternalLayoutLoader에서 가져올 수 있도록 개선 필요
  return [...internalLayouts, ...externalLayouts];
};

// 외부 레이아웃 등록 함수 export
export { registerExternalLayout, unregisterExternalLayout, getExternalLayouts } from './ExternalLayoutLoader';

const LayoutRegistry = {
  getLayout,
  getAllLayoutNames
};

export default LayoutRegistry; 